import React, { useEffect, useState } from 'react';
import api from '../../lib/api';

type WalletData = {
  wallet: {
    id: string;
    totalEarned: number;
    totalPaid: number;
    pendingBalance: number;
  };
  transactions: Array<{
    id: string;
    type: string;
    amountRM: number;
    status: string;
    description: string;
    referenceType: string;
    referenceId: string;
    createdAt: string;
  }>;
};

const pageHeader = (
  <>
    <h1 className="page-title">My Earnings</h1>
    <p className="text-glass-text-2 text-sm mt-1">Track your income and payment history</p>
  </>
);

const Earnings: React.FC = () => {
  const [walletData, setWalletData] = useState<WalletData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWallet = async () => {
      try {
        setLoading(true);
        const res = await api.get<WalletData>('/wallets/my-wallet');
        setWalletData(res.data);
      } catch (e: unknown) {
        const err = e as { response?: { data?: { error?: string } } };
        setError(err.response?.data?.error || 'Failed to load earnings');
      } finally {
        setLoading(false);
      }
    };
    fetchWallet();
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'status-pill status-pill-pos';
      case 'PENDING':
        return 'status-pill status-pill-warn';
      case 'FAILED':
        return 'status-pill status-pill-danger';
      default:
        return 'status-pill status-pill-muted';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'EARNING':
        return 'Earning';
      case 'PAYOUT':
        return 'Payout';
      case 'ADJUSTMENT':
        return 'Adjustment';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 text-glass-text">
        <div>{pageHeader}</div>
        <div className="glass-card p-12 text-center text-glass-text-2">Loading earnings data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 text-glass-text">
        <div>{pageHeader}</div>
        <div className="text-sm text-red-600 bg-red-500/10 border border-red-500/30 rounded-lg p-3">{error}</div>
      </div>
    );
  }

  const totalEarned = walletData?.wallet?.totalEarned || 0;
  const totalPaid = walletData?.wallet?.totalPaid || 0;
  const pendingBalance = walletData?.wallet?.pendingBalance || 0;
  const pendingCount = walletData?.transactions?.filter((t) => t.status === 'PENDING').length || 0;

  return (
    <div className="space-y-6 text-glass-text">
      <div>{pageHeader}</div>

      {!walletData?.wallet ? (
        <div className="glass-card p-8 text-center">
          <p className="text-glass-text-2">No earnings data found. Complete jobs to start earning!</p>
        </div>
      ) : (
        <>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="glass-card p-6 bg-gradient-to-br from-accent2/25 to-[rgb(var(--info)/0.2)] border-accent2/30">
              <h3 className="text-sm text-glass-text-2 mb-2">Total Earnings</h3>
              <p className="stat-display text-3xl">RM {totalEarned.toLocaleString()}</p>
              <p className="text-xs text-glass-text-3 mt-2">Lifetime earnings</p>
            </div>
            <div className="glass-card p-6">
              <h3 className="text-sm text-glass-text-2 mb-2">Pending Payments</h3>
              <p className="stat-display text-3xl text-[rgb(var(--warn))]">RM {pendingBalance.toLocaleString()}</p>
              <p className="text-xs text-glass-text-3 mt-2">
                {pendingCount} payment{pendingCount !== 1 ? 's' : ''} awaiting
              </p>
            </div>
            <div className="glass-card p-6">
              <h3 className="text-sm text-glass-text-2 mb-2">Total Received</h3>
              <p className="stat-display text-3xl text-[rgb(var(--pos))]">RM {totalPaid.toLocaleString()}</p>
              <p className="text-xs text-glass-text-3 mt-2">Successfully received</p>
            </div>
            <div className="glass-card p-6">
              <h3 className="text-sm text-glass-text-2 mb-2">Transactions</h3>
              <p className="stat-display text-3xl">{walletData.transactions.length}</p>
              <p className="text-xs text-glass-text-3 mt-2">Total records</p>
            </div>
          </div>

          <div className="glass-card overflow-hidden">
            <div className="px-6 py-4 border-b border-glass">
              <h2 className="page-title text-lg">Transaction History</h2>
              <p className="text-sm text-glass-text-2">Your payment earnings and records</p>
            </div>

            {walletData.transactions.length === 0 ? (
              <div className="p-8 text-center text-glass-text-2">
                No transactions yet. Complete job contracts to see earnings here.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-glass bg-glass-2/30">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-glass">
                    {walletData.transactions.map((tx) => (
                      <tr key={tx.id} className="hover:bg-glass-3/40 transition-colors">
                        <td className="px-6 py-4 text-sm text-glass-text-2">
                          {new Date(tx.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-glass-text">{getTypeLabel(tx.type)}</td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-glass-text">{tx.description || '-'}</div>
                          <div className="text-xs text-glass-text-3">{tx.referenceType}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-sm font-semibold ${tx.type === 'EARNING' ? 'text-accent2' : 'text-glass-text'}`}>
                            {tx.type === 'EARNING' ? '+' : tx.type === 'PAYOUT' ? '-' : ''}
                            RM {tx.amountRM.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={getStatusBadge(tx.status)}>{tx.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="glass-card p-6 border-[rgb(var(--info)/0.35)] bg-[rgb(var(--info)/0.08)]">
            <h3 className="font-semibold text-glass-text mb-1">Payment Records</h3>
            <p className="text-sm text-glass-text-2">
              All your earnings are tracked here. Payments are recorded manually by the agency. Contact your agency if
              you have questions about payment status.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default Earnings;
